import {
  ArrowRightLeft,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  StickyNote,
  Clock,
  Paperclip,
  ExternalLink,
  MessageSquareReply,
  MessagesSquare,
  CheckCircle2,
  Download,
  X,
  Shuffle,
  TrendingDown,
  TrendingUp,
  AlarmClock,
  RefreshCcw,
  IndianRupee,
  Link2,
  Users,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "@/stores/hook";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import {
  setLeadFilterField,
  applyFilters,
} from "@/features/leads/stores/leadFilterSlice";
import axiosInstance from "@/lib/axios";
import { childEndpoints } from "@/config/endpoints";
import { cn } from "@/lib/utils";
import { getInitials } from "@/utils/convetors";
import type { NotificationPayload, ServerNotification } from "../types";

/** Lead: show lead name. Child: show "(Child) leadName" using parent name when available. */
function NotificationSubjectLabel({ p }: { p: NotificationPayload }) {
  const name = (p.leadName?.trim() || p.title).trim() || "—";
  if (p.entityType === "lead") {
    return (
      <strong className="font-medium text-[var(--color-black-9)]">{name}</strong>
    );
  }
  return (
    <>
      <span className="font-medium text-[var(--color-black-7)]">(Child)</span>{" "}
      <strong className="font-medium text-[var(--color-black-9)]">{name}</strong>
    </>
  );
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/**
 * A follow-up is OVERDUE only once its whole calendar day has passed — the same
 * boundary the dashboard buckets, the leads-page filter and the board label use
 * (`getFollowUpStatDateFilter`: Overdue = `lt: startOfToday`). The old test was
 * `date < now`, which called this morning's follow-up "overdue" by lunchtime
 * while every other surface still counted it as Today, and would have called
 * EVERY follow-up overdue in a date-only tenant (those rows sit at 00:00).
 */
function isBeforeToday(iso?: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return d.getTime() < startOfToday.getTime();
}

const TYPE_ICON_MAP: Record<string, any> = {
  stage_change: ArrowRightLeft,
  status_change: ArrowRightLeft,
  sub_status_change: ArrowRightLeft,
  note: StickyNote,
  chat: MessagesSquare,
  call_history: Phone,
  followup: Clock,
  attachment: Paperclip,
  automation_shuffle: Shuffle,
  automation_shuffle_warning: AlarmClock,
  lead_recaptured: RefreshCcw,
  installment_due: IndianRupee,
  shared_view: Link2,
  hr: Users,
};

const TYPE_COLOR_MAP: Record<string, string> = {
  stage_change: "bg-blue-50 text-blue-600",
  status_change: "bg-emerald-50 text-emerald-600",
  sub_status_change: "bg-violet-50 text-violet-600",
  note: "bg-amber-50 text-amber-600",
  chat: "bg-indigo-50 text-indigo-600",
  call_history: "bg-cyan-50 text-cyan-600",
  followup: "bg-orange-50 text-orange-600",
  attachment: "bg-slate-100 text-slate-600",
  automation_shuffle: "bg-teal-50 text-teal-600",
  automation_shuffle_warning: "bg-amber-50 text-amber-600",
  lead_recaptured: "bg-orange-50 text-orange-600",
  installment_due: "bg-rose-50 text-rose-600",
  shared_view: "bg-teal-50 text-teal-600",
  // HR's own icon chip (features/hr/config/hr.theme HR_ICON_CHIP).
  hr: "bg-[#e7f1eb] text-[#218760]",
};

/**
 * An HR notice opens its `link` — an in-app path the server writes (e.g.
 * "/hr/attendance?tab=leave"). Anything that is not an app-relative path
 * falls back to HR's home rather than leaving the app.
 */
function hrLinkOf(p: NotificationPayload): string {
  const link = typeof p.link === "string" ? p.link.trim() : "";
  return link.startsWith("/") && !link.startsWith("//") ? link : "/hr";
}

interface NotificationItemProps {
  notification: ServerNotification;
  onDismiss: (id: number) => void;
  onMarkRead: (id: number) => void;
  onClose: () => void;
}

function CallIcon({ type }: { type?: string }) {
  if (type === "missed") return <PhoneMissed className="size-3" />;
  if (type === "incoming") return <PhoneIncoming className="size-3" />;
  if (type === "outgoing") return <PhoneOutgoing className="size-3" />;
  return <Phone className="size-3" />;
}

export default function NotificationItem({
  notification: n,
  onDismiss,
  onMarkRead,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const Icon = TYPE_ICON_MAP[n.type] ?? ArrowRightLeft;
  const colorClass = TYPE_COLOR_MAP[n.type] ?? "bg-slate-50 text-slate-600";
  const isRead = !!n.readAt;
  const p = n.payload;

  /**
   * Resolve the lead primary key for GET /core/leads/:id.
   * Child-scoped notifications store entityId = child id; payload.leadId is the parent when present.
   */
  async function resolveTargetLeadId(): Promise<number | null> {
    if (p.entityType === "lead") return p.entityId ?? null;
    if (p.leadId != null) return p.leadId;
    if (p.entityId == null) return null;
    try {
      const { data } = await axiosInstance.get(`${childEndpoints}/${p.entityId}`);
      const lid = data?.child?.leadId;
      return typeof lid === "number" ? lid : null;
    } catch {
      return null;
    }
  }

  async function navigateToEntity() {
    if (!isRead) onMarkRead(n.id);
    const targetLeadId = await resolveTargetLeadId();
    onClose();
    // Defer opening the lead sheet until after this click/pointer sequence completes.
    // Otherwise the tail of the same gesture can hit the dialog overlay and Radix closes the sheet immediately (flicker).
    window.setTimeout(() => {
      if (targetLeadId != null) {
        dispatch(setCurrentLeadId(targetLeadId));
      }
      if (!location.pathname.startsWith("/leads")) {
        navigate("/leads");
      }
    }, 16);
  }

  /**
   * Shuffle deep-links. "Lost" = the shuffle soft-revoked the user's row →
   * Revoked tab, Reassigned bucket. "Moved" = branch move; the assignment
   * survives but branch scope hides the lead → Revoked tab, All bucket
   * (active-but-hidden half). "Gained" = the active list.
   */
  function navigateToShuffleLeads(target: "lost" | "gained" | "moved") {
    if (!isRead) onMarkRead(n.id);
    onClose();
    window.setTimeout(() => {
      dispatch(
        setLeadFilterField({ key: "isRevoked", value: target !== "gained" })
      );
      dispatch(setLeadFilterField({ key: "isReenquired", value: false }));
      dispatch(
        setLeadFilterField({
          key: "revokeType",
          value: target === "lost" ? "reassigned" : "all",
        })
      );
      dispatch(applyFilters());
      if (!location.pathname.startsWith("/leads")) {
        navigate("/leads");
      }
    }, 16);
  }

  function renderBody() {
    switch (n.type) {
      case "stage_change":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            moved <NotificationSubjectLabel p={p} />{" "}
            {p.fromValue && (
              <>
                from{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                  {p.fromValue}
                </span>{" "}
              </>
            )}
            {p.toValue && (
              <>
                to{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-medium">
                  {p.toValue}
                </span>
              </>
            )}
          </span>
        );
      case "status_change":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            changed status of <NotificationSubjectLabel p={p} />{" "}
            {p.fromValue && (
              <>
                from{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium">
                  {p.fromValue}
                </span>{" "}
              </>
            )}
            {p.toValue && (
              <>
                to{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-medium">
                  {p.toValue}
                </span>
              </>
            )}
          </span>
        );
      case "sub_status_change":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            changed sub-status of <NotificationSubjectLabel p={p} />{" "}
            {p.fromValue && (
              <>
                from{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-medium">
                  {p.fromValue}
                </span>{" "}
              </>
            )}
            {p.toValue && (
              <>
                to{" "}
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-medium">
                  {p.toValue}
                </span>
              </>
            )}
          </span>
        );
      case "note":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            added a note on <NotificationSubjectLabel p={p} />
            {p.preview && (
              <span className="block mt-0.5 text-[10px] text-[var(--color-black-5)] italic truncate max-w-[260px]">
                "{p.preview}"
              </span>
            )}
          </span>
        );
      case "chat":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            sent a message about <NotificationSubjectLabel p={p} />
            {p.preview && (
              <span className="block mt-0.5 text-[10px] text-[var(--color-black-5)] truncate max-w-[260px]">
                {p.preview}
              </span>
            )}
          </span>
        );
      case "call_history":
        return (
          <span className="flex items-center gap-1 flex-wrap">
            <CallIcon type={p.preview} />
            <span>
              {p.preview === "missed" ? (
                <span className="text-red-500 font-medium">Missed call</span>
              ) : p.preview === "incoming" ? (
                "Incoming call"
              ) : (
                "Outgoing call"
              )}{" "}
              with <NotificationSubjectLabel p={p} />
            </span>
          </span>
        );
      case "installment_due":
        // `fromValue` carries the due date, exactly as the follow-up row does,
        // so the same past/future test decides the wording.
        return (
          <span>
            Instalment{" "}
            {p.fromValue && new Date(p.fromValue) < new Date() ? (
              <span className="text-red-500 font-medium">overdue</span>
            ) : (
              <span className="text-orange-500 font-medium">due</span>
            )}{" "}
            for <NotificationSubjectLabel p={p} />
          </span>
        );
      case "followup":
        return (
          <span>
            Follow-up{" "}
            {isBeforeToday(p.fromValue) ? (
              <span className="text-red-500 font-medium">overdue</span>
            ) : (
              <span className="text-orange-500 font-medium">due</span>
            )}{" "}
            for <NotificationSubjectLabel p={p} />
          </span>
        );
      case "attachment":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            uploaded{" "}
            {p.preview && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium gap-0.5">
                <Paperclip className="size-2.5" />
                {p.preview}
              </span>
            )}{" "}
            on <NotificationSubjectLabel p={p} />
          </span>
        );
      case "automation_shuffle":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.automationName ?? "An automation"}
            </strong>{" "}
            reshuffled leads —{" "}
            {(p.lostCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-medium mr-1">
                <TrendingDown className="size-2.5" />
                lost {p.lostCount}
              </span>
            )}
            {(p.gainedCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium mr-1">
                <TrendingUp className="size-2.5" />
                gained {p.gainedCount}
              </span>
            )}
            {(p.movedCount ?? 0) > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium">
                {p.movedCount} moved branch
              </span>
            )}
            {p.conditionText && (
              <span className="block mt-0.5 text-[10px] text-[var(--color-black-5)] italic">
                Why: {p.conditionText}
              </span>
            )}
          </span>
        );
      case "shared_view":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.actorName}
            </strong>{" "}
            shared the list{" "}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-medium">
              {p.shareLabel ?? "a view"}
            </span>{" "}
            with you
            {p.preview && (
              <span className="block mt-0.5 text-[10px] text-[var(--color-black-5)] italic">
                “{p.preview}”
              </span>
            )}
          </span>
        );
      case "hr":
        // Server-written title + body (leave / WFH / payslip / automation
        // notices) — printed verbatim, like every other HR label.
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.title}
            </strong>
            {p.body && (
              <span className="block mt-0.5 line-clamp-3">{p.body}</span>
            )}
          </span>
        );
      case "automation_shuffle_warning":
        return (
          <span>
            <strong className="font-medium text-[var(--color-black-9)]">
              {p.automationName ?? "An automation"}
            </strong>{" "}
            is about to reshuffle{" "}
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium">
              <AlarmClock className="size-2.5" />
              {p.count ?? 0} of your lead{(p.count ?? 0) === 1 ? "" : "s"}
            </span>{" "}
            — act on them to keep them
            {p.conditionText && (
              <span className="block mt-0.5 text-[10px] text-[var(--color-black-5)] italic">
                Why: {p.conditionText}
              </span>
            )}
          </span>
        );
      default:
        return <span>{p.body}</span>;
    }
  }

  /** A shared list opens on its own route — there is no lead behind it. */
  function navigateToSharedView() {
    if (!p.shareToken) return;
    if (!isRead) onMarkRead(n.id);
    onClose();
    window.setTimeout(() => navigate(`/shared/${p.shareToken}`), 16);
  }

  /** An HR notice opens its own in-app link — there is no lead behind it. */
  function navigateToHrLink() {
    if (!isRead) onMarkRead(n.id);
    onClose();
    const link = hrLinkOf(p);
    window.setTimeout(() => navigate(link), 16);
  }

  function renderQuickActions() {
    const actions: React.ReactNode[] = [];

    switch (n.type) {
      case "hr":
        actions.push(
          <button
            type="button"
            key="open-hr"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--color-success)] text-white text-[10px] font-medium hover:opacity-90 transition-opacity"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToHrLink();
            }}
          >
            <ExternalLink className="size-2.5" />
            Open
          </button>
        );
        // Entity-less — the generic "View" would open nothing.
        return actions;
      case "shared_view":
        if (p.shareToken) {
          actions.push(
            <button
              type="button"
              key="open-shared-view"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--color-success)] text-white text-[10px] font-medium hover:opacity-90 transition-opacity"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToSharedView();
              }}
            >
              <ExternalLink className="size-2.5" />
              Open view
            </button>
          );
        }
        // Entity-less — the generic "View" would open nothing.
        return actions;
      case "note":
        actions.push(
          <button
            type="button"
            key="reply"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--color-black-1)] text-[var(--color-black-7)] text-[10px] font-medium hover:bg-[var(--color-green-1)] transition-colors"
          >
            <MessageSquareReply className="size-2.5" />
            Reply
          </button>
        );
        break;
      case "chat":
        actions.push(
          <button
            type="button"
            key="open-chat"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-600 text-white text-[10px] font-medium hover:bg-indigo-700 transition-colors"
          >
            <MessagesSquare className="size-2.5" />
            Open chat
          </button>
        );
        actions.push(
          <button
            type="button"
            key="reply-chat"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--color-black-1)] text-[var(--color-black-7)] text-[10px] font-medium hover:bg-[var(--color-green-1)] transition-colors"
          >
            <MessageSquareReply className="size-2.5" />
            Reply
          </button>
        );
        break;
      case "call_history":
        if (p.preview === "missed") {
          actions.push(
            <button
              type="button"
              key="callback"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cyan-500 text-white text-[10px] font-medium hover:opacity-90 transition-opacity"
            >
              <Phone className="size-2.5" />
              Call Back
            </button>
          );
        }
        break;
      case "followup":
        actions.push(
          <button
            type="button"
            key="done"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--color-black-1)] text-[var(--color-black-7)] text-[10px] font-medium hover:bg-[var(--color-green-1)] transition-colors"
          >
            <CheckCircle2 className="size-2.5" />
            Done
          </button>
        );
        break;
      case "attachment":
        actions.push(
          <button
            type="button"
            key="download"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--color-black-1)] text-[var(--color-black-7)] text-[10px] font-medium hover:bg-[var(--color-green-1)] transition-colors"
          >
            <Download className="size-2.5" />
            Download
          </button>
        );
        break;
      case "automation_shuffle":
        if ((p.lostCount ?? 0) > 0) {
          actions.push(
            <button
              type="button"
              key="view-lost"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-700 text-[10px] font-medium hover:bg-red-100 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToShuffleLeads("lost");
              }}
            >
              <TrendingDown className="size-2.5" />
              View lost leads
            </button>
          );
        }
        if ((p.gainedCount ?? 0) > 0) {
          actions.push(
            <button
              type="button"
              key="view-gained"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-medium hover:bg-emerald-100 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToShuffleLeads("gained");
              }}
            >
              <TrendingUp className="size-2.5" />
              View gained leads
            </button>
          );
        }
        // Entity-less aggregate — the buttons above ARE the navigation;
        // skip the generic "View" action.
        return actions;
    }

    actions.push(
      <button
        type="button"
        key="view"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[var(--color-black-1)] text-[var(--color-black-7)] text-[10px] font-medium hover:bg-[var(--color-green-1)] transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void navigateToEntity();
        }}
      >
        <ExternalLink className="size-2.5" />
        View
      </button>
    );

    return actions;
  }

  return (
    <div
      className={cn(
        "group relative flex gap-2.5 px-3 py-2.5 border-b border-[var(--color-black-1)] hover:bg-[#f7f9f8] transition-colors cursor-pointer",
        !isRead && "bg-[var(--color-green-1)]/50"
      )}
      onClick={() => {
        if (n.type === "shared_view") {
          navigateToSharedView();
        } else if (n.type === "hr") {
          navigateToHrLink();
        } else if (n.type === "automation_shuffle") {
          navigateToShuffleLeads(
            (p.lostCount ?? 0) > 0
              ? "lost"
              : (p.movedCount ?? 0) > 0
                ? "moved"
                : "gained"
          );
        } else {
          void navigateToEntity();
        }
      }}
    >
      {!isRead && (
        <span className="absolute left-1 top-3.5 size-1.5 rounded-full bg-[var(--color-primary)]" />
      )}

      <div
        className={cn(
          "shrink-0 size-7 rounded-full flex items-center justify-center text-[10px] font-medium mt-0.5",
          colorClass
        )}
      >
        {/* HR notices carry the suite's icon, not the actor's initials —
            their actor is usually the system ("Xale HR"). */}
        {p.actorName && n.type !== "automation_shuffle" && n.type !== "hr" ? (
          <span className="text-[10px] font-semibold">
            {getInitials(p.actorName)}
          </span>
        ) : (
          <Icon className="size-3.5" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[11px] leading-[1.5] text-[var(--color-black-6)]">
          {renderBody()}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {renderQuickActions()}
          <span className="text-[10px] text-[var(--color-black-4)] ml-auto shrink-0">
            {timeAgo(n.createdAt)}
          </span>
        </div>
      </div>

      <button
        type="button"
        className="opacity-0 group-hover:opacity-100 absolute top-1.5 right-1.5 p-0.5 rounded hover:bg-[var(--color-black-1)] transition-all"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDismiss(n.id);
        }}
      >
        <X className="size-3 text-[var(--color-black-5)]" />
      </button>
    </div>
  );
}
